import { get } from "lodash";
import type { ChangeEvent } from "react";
import { unstable_batchedUpdates } from "react-dom";
import {
  makeFieldOptional,
  makeFieldRequired,
  makeFieldTypeOptional,
  makeFieldTypeRequired,
  type PathArray,
} from "common/utils/formStructure";
import {
  getSchemaPropertyFormPath,
  useBuilderFormContext,
} from "../../../form/form";
import type { UnitsOptions } from "./types";
import { addNullOption, getUnitsOptionsPath, removeNullOption } from "./utils";

type FieldType = string | Array<string>;

export function useOnRequiredChange(fieldPath: PathArray) {
  const { getValues, setValue } = useBuilderFormContext();

  return (e: ChangeEvent<HTMLInputElement>) => {
    const values = getValues();

    // this is the array of required values within the field itself
    const subschemaRequiredPath = getSchemaPropertyFormPath(
      fieldPath,
      "required",
    );
    const requiredArray = get(values, subschemaRequiredPath, []);

    const valueFieldTypePath = getSchemaPropertyFormPath(
      [...fieldPath, "value"],
      "type",
    );
    const valueFieldType = get(values, valueFieldTypePath) as FieldType;

    const unitsFieldTypePath = getSchemaPropertyFormPath(
      [...fieldPath, "units"],
      "type",
    );
    const unitsFieldType = get(values, unitsFieldTypePath) as FieldType;

    const unitsOptionsPath = getUnitsOptionsPath(fieldPath);
    const unitsOptions = get(values, unitsOptionsPath) as
      | UnitsOptions
      | undefined;

    if (e.target.checked) {
      const newRequiredArray = makeFieldRequired(
        "value",
        makeFieldRequired("units", requiredArray),
      );
      const newValueFieldType = makeFieldTypeRequired(valueFieldType);
      const newUnitsFieldType = makeFieldTypeRequired(unitsFieldType);

      unstable_batchedUpdates(() => {
        setValue(subschemaRequiredPath, newRequiredArray);
        setValue(valueFieldTypePath, newValueFieldType);
        setValue(unitsFieldTypePath, newUnitsFieldType);
        if (unitsOptions) {
          setValue(unitsOptionsPath, removeNullOption(unitsOptions));
        }
      });
    } else {
      const newRequiredArray = makeFieldOptional(
        "value",
        makeFieldOptional("units", requiredArray),
      );
      const newValueFieldType = makeFieldTypeOptional(valueFieldType);
      const newUnitsFieldType = makeFieldTypeOptional(unitsFieldType);

      unstable_batchedUpdates(() => {
        setValue(subschemaRequiredPath, newRequiredArray);
        setValue(valueFieldTypePath, newValueFieldType);
        setValue(unitsFieldTypePath, newUnitsFieldType);
        if (unitsOptions) {
          setValue(unitsOptionsPath, addNullOption(unitsOptions));
        }
      });
    }
  };
}
